// Core Modules
import { Route, Routes } from '@angular/router';

// Guards
import { AuthGuard } from '@core/guards/auth/auth.guard';
import { PermissionGuard } from '@core/guards/permission/permission.guard';
import { SessionValidationGuard } from '@core/guards/session-validation/session-validation.guard';

// Resolvers
import { FederatedDomainResolver } from '@core/resolvers/federated-domain.resolver';

// Components
import { AuthLoginComponent } from '@shared/components/auth-login/auth-login.component';
import { UnauthorizedComponent } from '@shared/components/unauthorized/unauthorized.component';

// Enums
import { MainNavigationEnum } from '@shared/enum/main-navigation-url.enum';

/**
 * Build a lazily-loaded, permission-gated feature-area route.
 * @param path route base (from MainNavigationEnum)
 * @param loadChildren dynamic import of the area's routes file
 * @param permission app-permission that gates the area
 * @returns the configured Route
 */
const featureRoutes = (path: string, loadChildren: Route['loadChildren'], permission: string): Route => ({
  path,
  loadChildren,
  canActivate: [SessionValidationGuard, PermissionGuard],
  data: { permissions: { only: [permission], redirectTo: MainNavigationEnum.UNAUTHORIZED } },
});

export const routes: Routes = [
  {
    path: MainNavigationEnum.LOGIN,
    component: AuthLoginComponent,
    canActivate: [AuthGuard],
    resolve: { data: FederatedDomainResolver },
    title: 'Login',
  },

  // Access & Identity — Users is the default landing
  featureRoutes(MainNavigationEnum.USERS, () => import('@features/users/users.routes').then((m) => m.routes), 'user-read'),
  featureRoutes(MainNavigationEnum.TOKENS, () => import('@features/tokens/tokens.routes').then((m) => m.routes), 'token-read'),

  // Clients
  featureRoutes(MainNavigationEnum.CLIENTS, () => import('@features/clients/clients.routes').then((m) => m.routes), 'client-read'),

  // Activity
  featureRoutes(MainNavigationEnum.HISTORICAL_PULL, () => import('@features/activity/activity.routes').then((m) => m.routes), 'historical-pull-read'),

  {
    path: MainNavigationEnum.UNAUTHORIZED,
    component: UnauthorizedComponent,
    pathMatch: 'full',
    canActivate: [SessionValidationGuard],
    title: 'Unauthorized',
  },
  // Redirect to /login if match is ""
  { path: '', redirectTo: `/${MainNavigationEnum.LOGIN}`, pathMatch: 'full' },
  // Redirect to /login if no route match is found
  { path: '**', redirectTo: `/${MainNavigationEnum.LOGIN}` },
];
